import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { User } from '../../../../core/interfaces/user.interface';
import { AppState } from '../../../../store/app.state';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { updateCurrentUserSuccess } from '../../../../store/auth/auth.actions';
import { AdministrativeData } from '../../../../core/interfaces/passport.interface';
import { PassportService } from '../../../../core/services/passport.service';
import { ToastService } from '../../../../core/services/toast.service';
import { removeEmptyProperties } from '../../../../utils/object.utils';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import { noWhitespaceValidator } from '../../../../shared/validators';

@Component({
  selector: 'app-administrative-data',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AngularPhoneNumberInput],
  templateUrl: './administrative-data.component.html',
  styleUrls: ['./administrative-data.component.scss'],
})
export class AdministrativeDataComponent implements OnInit {
  administrativeForm!: FormGroup;

  currentUser$!: Observable<User | null>;
  loading: boolean = false;

  private store = inject(Store<AppState>);
  private fb = inject(FormBuilder);
  private passportService = inject(PassportService);
  private toastService = inject(ToastService);

  constructor() {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.currentUser$ = this.store.select(selectCurrentUser);

    // Attendre un peu que le store soit initialisé
    setTimeout(() => {
      this.populateFormWithUserData();
    }, 100);

    // S'abonner aux changements du currentUser pour recharger les données si nécessaire
    this.currentUser$.subscribe((user) => {
      if (user && !this.administrativeForm.get('firstName')?.value) {
        console.log('Rechargement des données utilisateur...');
        this.populateFormWithUserData();
      }
    });

    this.setupAgeCalculation();
    this.setupPhoneNumberListener();
  }

  private initializeForm(): void {
    this.administrativeForm = this.fb.group({
      // Informations principales
      firstName: ['', [Validators.required, noWhitespaceValidator]],
      lastName: ['', [Validators.required, noWhitespaceValidator]],
      dateOfBirth: ['', Validators.required],
      age: [''],
      sex: ['', Validators.required],
      placeOfBirth: [''],
      phone: ['', Validators.required],
      landline: [''],
      email: ['', Validators.email],
      maritalStatus: [''],
      bloodGroup: [''],
      weight: [''],
      height: [''],

      // Adresse actuelle
      currentCountry: [''],
      currentRegion: [''],
      currentDepartment: [''],
      currentCommune: [''],
      currentDistrict: [''],

      // Adresse habituelle
      usualCountry: [''],
      usualRegion: [''],
      usualDepartment: [''],
      usualCommune: [''],
      usualDistrict: [''],

      // Informations complémentaires
      educationLevel: [''],
      profession: [''],
      nationality: [''],
      ethnicity: [''],
    });
  }

  private populateFormWithUserData(): void {
    this.currentUser$.pipe(take(1)).subscribe((user) => {
      if (user) {
        console.log('Données utilisateur reçues:', user);

        // Convertir la date de naissance du format DD/MM/YYYY vers YYYY-MM-DD
        const birthday = user.birthday
          ? this.convertDateToISO(user.birthday)
          : '';

        // Calculer l'âge
        const age = user.birthday ? this.calculateAge(user.birthday) : '';

        // Récupérer le numéro de téléphone principal
        const primaryPhone =
          user.phoneNumbers.find((p) => p.isPrimary)?.number || '';

        // Récupérer le numéro de téléphone fixe
        const landlinePhone = user.phoneNumbers.find((p) => p.type === 'FIXE');
        const landlineValue = landlinePhone
          ? `+${landlinePhone.countryCode}${landlinePhone.number}`
          : '';

        // Récupérer les adresses (utiliser any pour contourner l'interface limitée)
        const currentAddress = (user.addresses as any[])?.find(
          (addr: any) => addr.isCurrent
        );
        const usualAddress = (user.addresses as any[])?.find(
          (addr: any) => !addr.isCurrent
        );

        // Mettre à jour les champs activés
        this.administrativeForm.patchValue({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          dateOfBirth: birthday,
          sex: user.sex || '',
          nationality: user.nationality?.code || '',
          weight: (user as any).weight || '',
          height: this.formatHeightForInput((user as any).height),
          bloodGroup: (user as any).bloodGroup?.code || '',
          maritalStatus: (user as any).maritalStatus?.code || '',
          ethnicity: (user as any).ethnicity || '',
          profession: (user as any).profession?.code || '',
          educationLevel: (user as any).studyLevel?.code || '',

          // Adresse actuelle
          currentCountry: currentAddress?.country?.code || '',
          currentRegion: currentAddress?.region || '',
          currentDepartment: currentAddress?.departement || '',
          currentCommune: currentAddress?.commune || '',
          currentDistrict: currentAddress?.street || '',

          // Adresse habituelle
          usualCountry: usualAddress?.country?.code || '',
          usualRegion: usualAddress?.region || '',
          usualDepartment: usualAddress?.departement || '',
          usualCommune: usualAddress?.commune || '',
          usualDistrict: usualAddress?.street || '',
        });

        // Mettre à jour les champs désactivés avec un délai
        setTimeout(() => {
          this.administrativeForm.get('age')?.setValue(age);
          this.administrativeForm.get('phone')?.setValue(primaryPhone);
          this.administrativeForm.get('email')?.setValue(user.email || '');

          // Mettre à jour le téléphone fixe
          if (landlineValue) {
            this.administrativeForm.get('landline')?.setValue(landlineValue);
          }
        }, 50);

        console.log('Formulaire mis à jour avec les valeurs:', {
          firstName: user.firstName,
          lastName: user.lastName,
          dateOfBirth: birthday,
          age: age,
          sex: user.sex,
          phone: primaryPhone,
          email: user.email,
          nationality: user.nationality?.code,
          landline: landlineValue,
          weight: (user as any).weight,
          height: (user as any).height,
          bloodGroup: (user as any).bloodGroup?.code,
          maritalStatus: (user as any).maritalStatus?.code,
          ethnicity: (user as any).ethnicity,
          profession: (user as any).profession?.code,
          educationLevel: (user as any).studyLevel?.code,
          currentAddress: currentAddress,
          usualAddress: usualAddress,
        });
      } else {
        console.log('Aucun utilisateur trouvé');
      }
    });
  }

  private convertDateToISO(dateString: string): string {
    if (!dateString) return '';

    // Convertir DD/MM/YYYY vers YYYY-MM-DD
    const parts = dateString.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(
        2,
        '0'
      )}`;
    }
    return '';
  }

  private convertDateToDDMMYYYY(dateString: string): string {
    if (!dateString) return '';

    // Convertir YYYY-MM-DD vers DD/MM/YYYY
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${day}/${month}/${year}`;
  }

  private calculateAge(birthday: string): number {
    if (!birthday) return 0;

    const parts = birthday.split('/');
    if (parts.length === 3) {
      const birthDate = new Date(
        parseInt(parts[2]),
        parseInt(parts[1]) - 1,
        parseInt(parts[0])
      );
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    }
    return 0;
  }

  onSubmit(): void {
    if (this.administrativeForm.valid) {
      this.loading = true;

      const formValue = this.administrativeForm.value;
      const normalizedHeight = this.normalizeHeightValue(formValue.height);
      const normalizedWeight = this.normalizeWeightValue(formValue.weight);

      // Convertir la date de YYYY-MM-DD vers DD/MM/YYYY
      const birthday = formValue.dateOfBirth
        ? this.convertDateToDDMMYYYY(formValue.dateOfBirth)
        : '';

      // Extraire le numéro de téléphone complet du composant angular-phone-number-input
      const landlineData = formValue.landline || {};

      console.log('Données du téléphone fixe:', landlineData);
      console.log('Type de landlineData:', typeof landlineData);
      console.log('landlineData est null?', landlineData === null);
      console.log('landlineData est undefined?', landlineData === undefined);
      console.log('landlineData est une chaîne vide?', landlineData === '');

      // Traiter les données du téléphone fixe
      let phoneNumberData = {
        countryCode: '229',
        number: '',
        type: 'FIXE',
        isPrimary: false,
      };

      if (typeof landlineData === 'object' && landlineData !== null) {
        console.log('Branche objet détectée');
        // Si c'est un objet avec les propriétés attendues
        if (landlineData.countryCode && landlineData.number) {
          console.log('Objet avec countryCode et number détecté');
          phoneNumberData = {
            countryCode: landlineData.countryCode,
            number: landlineData.number,
            type: 'FIXE',
            isPrimary: false,
          };
        } else {
          console.log('Objet sans countryCode ou number valides');
        }
      } else if (typeof landlineData === 'string' && landlineData) {
        console.log('Branche chaîne détectée');
        // Si c'est une chaîne (comme dans verify-phone-number)
        console.log('Traitement de la chaîne téléphone:', landlineData);
        const { countryCode, number } = this.extractPhoneData(landlineData);
        console.log('Résultat extraction:', { countryCode, number });
        phoneNumberData = {
          countryCode: countryCode,
          number: number,
          type: 'FIXE',
          isPrimary: false,
        };
      } else {
        console.log('Aucune branche détectée - valeurs par défaut utilisées');
      }

      const payload: AdministrativeData = {
        firstName: formValue.firstName || '',
        lastName: formValue.lastName || '',
        birthday: birthday,
        placeOfBirth: formValue.placeOfBirth || '',
        sex: formValue.sex || '',
        phoneNumbers: [phoneNumberData],
        maritalStatus: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.maritalStatus || '',
          label:
            this.maritalStatusOptions.find(
              (opt) => opt.value === formValue.maritalStatus
            )?.label || '',
          message: '',
        },
        bloodGroup: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.bloodGroup || '',
          label:
            this.bloodGroupOptions.find(
              (opt) => opt.value === formValue.bloodGroup
            )?.label || '',
          message: '',
        },
        ...(normalizedHeight !== undefined ? { height: normalizedHeight } : {}),
        ...(normalizedWeight !== undefined ? { weight: normalizedWeight } : {}),
        addresses: this.buildAddressesArray(formValue),
        studyLevel: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.educationLevel || '',
          label:
            this.educationLevelOptions.find(
              (opt) => opt.value === formValue.educationLevel
            )?.label || '',
          message: '',
        },
        profession: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.profession || '',
          label:
            this.professionOptions.find(
              (opt) => opt.value === formValue.profession
            )?.label || '',
          message: '',
        },
        nationality: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.nationality || '',
          label:
            this.nationalityOptions.find(
              (opt) => opt.value === formValue.nationality
            )?.label || '',
          message: '',
        },
        ethnicity: formValue.ethnicity || '',
      };

      // Nettoyer le payload en supprimant les propriétés vides
      const cleanedPayload = removeEmptyProperties(
        payload
      ) as AdministrativeData;

      console.log('Payload original:', payload);
      console.log('Payload nettoyé:', cleanedPayload);
      console.log('Valeurs du formulaire:', formValue);

      // Récupérer le passportId depuis le currentUser
      this.currentUser$.pipe(take(1)).subscribe((user) => {
        if (user && user.passportNumber) {
          this.passportService
            .updateAdministrativeDatas(user.passportNumber, cleanedPayload)
            .subscribe({
              next: (response) => {
                this.loading = false;
                console.log(
                  'Données administratives mises à jour avec succès:',
                  response
                );

                // Mettre à jour l'utilisateur dans le store avec les nouvelles données
                this.currentUser$.pipe(take(1)).subscribe((currentUser) => {
                  if (currentUser) {
                    const responseUser = response?.data ?? {};

                    // Créer un objet utilisateur mis à jour avec les nouvelles données
                    const updatedUser = {
                      ...currentUser,
                      ...responseUser,
                      firstName:
                        responseUser.firstName ||
                        formValue.firstName ||
                        currentUser.firstName,
                      lastName:
                        responseUser.lastName ||
                        formValue.lastName ||
                        currentUser.lastName,
                      birthday:
                        responseUser.birthday || birthday || currentUser.birthday,
                      height:
                        responseUser.height ?? normalizedHeight ?? currentUser.height,
                      weight:
                        responseUser.weight ?? normalizedWeight ?? currentUser.weight,
                      phoneNumbers:
                        responseUser.phoneNumbers ||
                        [
                          ...(currentUser.phoneNumbers || []),
                          phoneNumberData,
                        ].filter((phone) => phone.number), // Filtrer les numéros vides
                    } as User;

                    // Dispatcher l'action de mise à jour de l'utilisateur
                    this.store.dispatch(
                      updateCurrentUserSuccess({ user: updatedUser })
                    );

                    console.log(
                      'Utilisateur mis à jour dans le store:',
                      updatedUser
                    );

                    this.administrativeForm.patchValue(
                      {
                        height: this.formatHeightForInput(updatedUser.height),
                        weight: updatedUser.weight ?? normalizedWeight ?? '',
                      },
                      { emitEvent: false }
                    );
                  }
                });

                this.toastService.success(
                  'Données administratives mises à jour avec succès !'
                );
              },
              error: (error) => {
                this.loading = false;
                console.error(
                  'Erreur lors de la mise à jour des données administratives:',
                  error
                );
                this.toastService.error(
                  'Erreur lors de la mise à jour des données administratives. Veuillez réessayer.'
                );
              },
            });
        } else {
          this.loading = false;
          console.error(
            'PassportNumber non trouvé dans les données utilisateur'
          );
          this.toastService.error('Erreur : Numéro de passeport non trouvé.');
        }
      });
    } else {
      this.markFormGroupTouched(this.administrativeForm);
    }
  }

  private setupAgeCalculation(): void {
    this.administrativeForm
      .get('dateOfBirth')
      ?.valueChanges.subscribe((date) => {
        if (date) {
          const age = this.calculateAgeFromISO(date);
          this.administrativeForm.patchValue(
            { age: age },
            { emitEvent: false }
          );
        }
      });
  }

  private normalizeHeightValue(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const normalizedValue =
      typeof value === 'string' ? value.trim().replace(',', '.') : value;
    const numericValue = Number(normalizedValue);

    if (!Number.isFinite(numericValue)) {
      return undefined;
    }

    if (numericValue > 3 && numericValue <= 250) {
      return Number((numericValue / 100).toFixed(2));
    }

    return numericValue >= 0.5 && numericValue <= 3
      ? Number(numericValue.toFixed(2))
      : undefined;
  }

  private normalizeWeightValue(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 1
      ? numericValue
      : undefined;
  }

  private formatHeightForInput(value: unknown): string {
    const normalizedHeight = this.normalizeHeightValue(value);

    if (normalizedHeight === undefined) {
      return '';
    }

    return normalizedHeight.toFixed(2).replace('.', ',');
  }

  private setupPhoneNumberListener(): void {
    this.administrativeForm.get('landline')?.valueChanges.subscribe((value) => {
      console.log('Téléphone fixe changé:', value);
      console.log('Type de valeur:', typeof value);

      if (typeof value === 'object' && value !== null) {
        console.log("Propriétés de l'objet:", Object.keys(value));
        for (const [key, val] of Object.entries(value)) {
          console.log(`${key}:`, val);
        }
      }
    });
  }

  onPhoneInputChange(event: any) {
    console.log('onPhoneInputChange - Event:', event);
    console.log('onPhoneInputChange - Type:', typeof event);
    console.log('onPhoneInputChange - Value:', event);

    // Si c'est un objet, afficher ses propriétés
    if (typeof event === 'object' && event !== null) {
      console.log('onPhoneInputChange - Object keys:', Object.keys(event));
      for (const [key, value] of Object.entries(event)) {
        console.log(`onPhoneInputChange - ${key}:`, value);
      }
    }
  }

  /**
   * Construit le tableau d'adresses avec l'adresse actuelle et l'adresse habituelle
   */
  private buildAddressesArray(formValue: any): any[] {
    const addresses = [];

    // Adresse actuelle
    if (
      formValue.currentCountry ||
      formValue.currentRegion ||
      formValue.currentDepartment ||
      formValue.currentCommune ||
      formValue.currentDistrict
    ) {
      addresses.push({
        street: formValue.currentDistrict || '',
        neighborhood: '',
        commune: formValue.currentCommune || '',
        departement: formValue.currentDepartment || '',
        region: formValue.currentRegion || '',
        country: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.currentCountry || '',
          label:
            this.countryOptions.find(
              (opt) => opt.value === formValue.currentCountry
            )?.label || '',
          message: '',
        },
        postalCode: '',
        additionalInfo: '',
        isCurrent: true,
      });
    }

    // Adresse habituelle
    if (
      formValue.usualCountry ||
      formValue.usualRegion ||
      formValue.usualDepartment ||
      formValue.usualCommune ||
      formValue.usualDistrict
    ) {
      addresses.push({
        street: formValue.usualDistrict || '',
        neighborhood: '',
        commune: formValue.usualCommune || '',
        departement: formValue.usualDepartment || '',
        region: formValue.usualRegion || '',
        country: {
          identifier: '',
          eyoneExternalId: '',
          code: formValue.usualCountry || '',
          label:
            this.countryOptions.find(
              (opt) => opt.value === formValue.usualCountry
            )?.label || '',
          message: '',
        },
        postalCode: '',
        additionalInfo: '',
        isCurrent: false,
      });
    }

    console.log('Adresses construites:', addresses);
    return addresses;
  }

  private calculateAgeFromISO(dateString: string): number {
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  }

  // Méthodes helper pour la validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.administrativeForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.administrativeForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return 'Ce champ est requis';
      }
      if (field.errors['email']) {
        return 'Veuillez entrer une adresse email valide';
      }
      if (field.errors['minlength']) {
        return `Minimum ${field.errors['minlength'].requiredLength} caractères`;
      }
    }
    return '';
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  // Options pour le sexe
  sexOptions = [
    { value: 'male', label: 'Masculin' },
    { value: 'female', label: 'Féminin' },
  ];

  // Options pour la situation matrimoniale
  maritalStatusOptions = [
    { value: 'single', label: 'Célibataire' },
    { value: 'married', label: 'Marié(e)' },
    { value: 'divorced', label: 'Divorcé(e)' },
    { value: 'widowed', label: 'Veuf/Veuve' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour le groupe sanguin
  bloodGroupOptions = [
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' },
  ];

  // Options pour les pays
  countryOptions = [
    { value: 'benin', label: 'Bénin' },
    { value: 'france', label: 'France' },
    { value: 'nigeria', label: 'Nigéria' },
    { value: 'togo', label: 'Togo' },
    { value: 'ghana', label: 'Ghana' },
    { value: 'ivory_coast', label: "Côte d'Ivoire" },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour les régions
  regionOptions = [
    { value: 'region1', label: 'Région 1' },
    { value: 'region2', label: 'Région 2' },
    { value: 'region3', label: 'Région 3' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour les départements
  departmentOptions = [
    { value: 'department1', label: 'Département 1' },
    { value: 'department2', label: 'Département 2' },
    { value: 'department3', label: 'Département 3' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour le niveau d'instruction
  educationLevelOptions = [
    { value: 'none', label: 'Aucun' },
    { value: 'primary', label: 'Primaire' },
    { value: 'secondary', label: 'Secondaire' },
    { value: 'highschool', label: 'Lycée' },
    { value: 'university', label: 'Université' },
    { value: 'vocational', label: 'Formation professionnelle' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour la profession
  professionOptions = [
    { value: 'student', label: 'Étudiant(e)' },
    { value: 'employee', label: 'Employé(e)' },
    { value: 'self_employed', label: 'Travailleur indépendant' },
    { value: 'unemployed', label: 'Sans emploi' },
    { value: 'retired', label: 'Retraité(e)' },
    { value: 'homemaker', label: 'Femme/Homme au foyer' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour la nationalité
  nationalityOptions = [
    { value: 'benin', label: 'Béninoise' },
    { value: 'france', label: 'Française' },
    { value: 'nigeria', label: 'Nigériane' },
    { value: 'togo', label: 'Togolaise' },
    { value: 'ghana', label: 'Ghanéenne' },
    { value: 'ivory_coast', label: 'Ivoirienne' },
    { value: 'other', label: 'Autre' },
  ];

  // Options pour l'ethnie
  ethnicityOptions = [
    { value: 'fon', label: 'Fon' },
    { value: 'yoruba', label: 'Yoruba' },
    { value: 'adja', label: 'Adja' },
    { value: 'bariba', label: 'Bariba' },
    { value: 'dendi', label: 'Dendi' },
    { value: 'goun', label: 'Goun' },
    { value: 'other', label: 'Autre' },
  ];

  /**
   * Extrait le code pays et le numéro d'une chaîne de téléphone complète
   * Basé sur la logique du composant verify-phone-number
   */
  private extractPhoneData(fullPhoneNumber: string): {
    countryCode: string;
    number: string;
  } {
    // Définir les indicatifs des pays préférés
    const countryCodes = {
      SN: '221', // Sénégal
      CI: '225', // Côte d'Ivoire
      CM: '237', // Cameroun
      BJ: '229', // Bénin
    };

    // Extraire l'indicatif et le numéro
    let indicative = '';
    let phoneNumber = '';

    // Essayer de trouver l'indicatif dans la chaîne
    for (const [country, code] of Object.entries(countryCodes)) {
      if (fullPhoneNumber.includes(code)) {
        const parts = fullPhoneNumber.split(code);
        if (parts.length > 1) {
          indicative = code;
          phoneNumber = parts[1].replace(/[^0-9]/g, ''); // Garder seulement les chiffres
          break;
        }
      }
    }

    // Si aucun indicatif trouvé, essayer une approche différente
    if (!indicative) {
      // Supprimer tous les caractères non numériques sauf le +
      const cleanNumber = fullPhoneNumber.replace(/[^\d+]/g, '');

      // Essayer de trouver un indicatif de 2-3 chiffres
      const match = cleanNumber.match(/^\+?(\d{2,3})(\d+)$/);
      if (match) {
        indicative = match[1];
        phoneNumber = match[2];
      } else {
        // Par défaut, utiliser le code du Bénin
        indicative = '229';
        phoneNumber = fullPhoneNumber.replace(/[^0-9]/g, '');
      }
    }

    return {
      countryCode: indicative,
      number: phoneNumber,
    };
  }
}
